import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import { supabase } from '../services/supabase'
import { useAuthStore } from '../stores/authStore'
import { CheckCircle, XCircle, Loader } from 'lucide-react'

const AuthCallbackPage: React.FC = () => {
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading')
  const [error, setError] = useState<string>('')
  
  const navigate = useNavigate()
  const { initialize } = useAuthStore()

  useEffect(() => {
    const handleAuthCallback = async () => {
      try {
        // 处理OAuth回调
        const { data, error } = await supabase.auth.getSession()
        
        if (error) {
          console.error('Auth callback error:', error)
          setError(error.message)
          setStatus('error')
          return
        }

        if (data.session) {
          // 认证成功
          await initialize() // 重新初始化认证状态
          setStatus('success')
          
          // 延迟跳转，让用户看到成功状态
          setTimeout(() => {
            navigate('/', { replace: true })
          }, 2000)
        } else {
          // 没有会话，可能是用户取消了认证
          setError('Authentication was cancelled or failed')
          setStatus('error')
        }
      } catch (err: any) {
        console.error('Unexpected error during auth callback:', err)
        setError(err.message || 'An unexpected error occurred')
        setStatus('error')
      }
    }

    handleAuthCallback()
  }, [navigate, initialize])

  const handleRetry = () => {
    navigate('/auth')
  }

  const handleGoHome = () => {
    navigate('/')
  }

  return (
    <>
      <Helmet>
        <title>Authentication - AI Decision Maker</title>
        <meta name="description" content="Processing your authentication" />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div className="text-center">
            {status === 'loading' && (
              <>
                <div className="mx-auto h-12 w-12 bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg flex items-center justify-center">
                  <Loader className="h-6 w-6 text-white animate-spin" />
                </div>
                <h2 className="mt-6 text-3xl font-extrabold text-gray-900">
                  Completing authentication...
                </h2>
                <p className="mt-2 text-sm text-gray-600">
                  Please wait while we complete your sign-in process.
                </p>
              </>
            )}

            {status === 'success' && (
              <>
                <div className="mx-auto h-12 w-12 bg-gradient-to-r from-green-600 to-blue-600 rounded-lg flex items-center justify-center">
                  <CheckCircle className="h-6 w-6 text-white" />
                </div>
                <h2 className="mt-6 text-3xl font-extrabold text-gray-900">
                  Authentication successful!
                </h2>
                <p className="mt-2 text-sm text-gray-600">
                  You have been successfully signed in. Redirecting to home page...
                </p>
                <div className="mt-4">
                  <button
                    onClick={handleGoHome}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors"
                  >
                    Go to Home Page
                  </button>
                </div>
              </>
            )}

            {status === 'error' && (
              <>
                <div className="mx-auto h-12 w-12 bg-gradient-to-r from-red-600 to-pink-600 rounded-lg flex items-center justify-center">
                  <XCircle className="h-6 w-6 text-white" />
                </div>
                <h2 className="mt-6 text-3xl font-extrabold text-gray-900">
                  Authentication failed
                </h2>
                <p className="mt-2 text-sm text-gray-600">
                  {error || 'There was an error during the authentication process.'}
                </p>
                <div className="mt-6 space-y-3">
                  <button
                    onClick={handleRetry}
                    className="w-full inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors"
                  >
                    Try Again
                  </button>
                  <button
                    onClick={handleGoHome}
                    className="w-full inline-flex justify-center items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors"
                  >
                    Go to Home Page
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  )
}

export default AuthCallbackPage 